import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBuildingById,
  createBuilding,
  updateBuilding,
  deleteBuilding,
  type BuildingFilters,
  type Building,
  getBuildings,
} from '../../services';

// Query keys factory
export const buildingKeys = {
  all: ['buildings'] as const,
  lists: () => [...buildingKeys.all, 'list'] as const,
  list: (filters: BuildingFilters) =>
    [...buildingKeys.lists(), filters] as const,
  details: () => [...buildingKeys.all, 'detail'] as const,
  detail: (id: number) => [...buildingKeys.details(), id] as const,
};

// List query with filters and pagination
export function useBuildings(filters: BuildingFilters) {
  return useQuery({
    queryKey: buildingKeys.list(filters),
    queryFn: () => getBuildings(filters),
    placeholderData: (previousData) => previousData,
  });
}

// Single building query
export function useBuilding(id: number) {
  return useQuery({
    queryKey: buildingKeys.detail(id),
    queryFn: () => getBuildingById(id),
    enabled: !!id,
  });
}

// Create mutation
export function useCreateBuilding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createBuilding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildingKeys.lists() });
    },
  });
}

// Update mutation
export function useUpdateBuilding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Building> }) =>
      updateBuilding(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: buildingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: buildingKeys.detail(id) });
    },
  });
}

// Delete mutation
export function useDeleteBuilding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteBuilding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildingKeys.lists() });
    },
  });
}
